<template>
  <div class="sec">Todas as Conversas</div>
  <div class="conv-panel">
    <div class="conv-head">
      <div class="conv-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        Conversas
        <span style="background:rgba(188,12,36,.08);color:var(--red);padding:2px 8px;border-radius:20px;font-size:11.5px;font-weight:700;">
          {{ filtered.length }}
        </span>
      </div>
      <div class="filters">
        <div class="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model="query"
            class="search-input"
            type="search"
            placeholder="Buscar contato, agente…"
          />
        </div>
        <select v-model="filterStatus" class="flt">
          <option value="">Todos os status</option>
          <option value="open">Aberta</option>
          <option value="resolved">Resolvida</option>
          <option value="pending">Pendente</option>
        </select>
        <select v-model="filterTeam" class="flt">
          <option value="">Todos os times</option>
          <option v-for="t in teamOptions" :key="t" :value="t">
            {{ t.charAt(0).toUpperCase() + t.slice(1) }}
          </option>
        </select>
        <select v-model="filterLabel" class="flt">
          <option value="">Todas as etiquetas</option>
          <option v-for="l in labelOptions" :key="l" :value="l">
            {{ l.replace(/_/g, ' ') }}
          </option>
        </select>
        <select v-model="filterFluxo" class="flt">
          <option value="">Todos os fluxos</option>
          <option v-for="f in fluxoOptions" :key="f.id" :value="f.id">{{ f.name }}</option>
        </select>
        <div class="date-range">
          <label>De</label>
          <input v-model="dateFrom" class="flt-date" type="date" />
          <label>até</label>
          <input v-model="dateTo"   class="flt-date" type="date" />
          <button class="btn-clear-date" title="Limpar datas" @click="clearDates">✕</button>
        </div>
      </div>
    </div>

    <div class="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Contato</th>
            <th>Agente</th>
            <th>Time</th>
            <th>Status</th>
            <th>Duração</th>
            <th>Aguardando</th>
            <th>Etiquetas</th>
            <th>Fluxo</th>
            <th style="width:72px"></th>
          </tr>
        </thead>
        <tbody>
          <!-- Skeleton: primeira carga sem dados -->
          <template v-if="!props.scopeReady && props.conversations.length === 0">
            <tr v-for="i in pageSize" :key="`skel-${i}`" class="skel-row">
              <td><div class="skel skel-sm"></div></td>
              <td>
                <div class="skel-contact">
                  <div class="skel skel-av"></div>
                  <div><div class="skel skel-md"></div><div class="skel skel-sm" style="margin-top:4px"></div></div>
                </div>
              </td>
              <td><div class="skel skel-md"></div></td>
              <td><div class="skel skel-sm"></div></td>
              <td><div class="skel skel-pill"></div></td>
              <td><div class="skel skel-sm"></div></td>
              <td><div class="skel skel-sm"></div></td>
              <td><div class="skel skel-md"></div></td>
              <td><div class="skel skel-sm"></div></td>
              <td></td>
            </tr>
          </template>

          <!-- Empty state: sem loading, sem dados -->
          <tr v-else-if="!pageConvs.length">
            <td colspan="10">
              <div class="empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <p>Nenhuma conversa encontrada</p>
              </div>
            </td>
          </tr>

          <!-- Linhas reais com v-memo para atualização parcial -->
          <tr
            v-for="c in pageConvs"
            :key="c.id"
            v-memo="[c.status, c.assignee?.id ?? null, c.team?.id ?? null, c.waiting_since, c.snoozed_until, c.labels.join(','), c.fluxo_id, props.changedIds?.has(c.id) ?? false]"
            :class="{ 'row-changed': props.changedIds?.has(c.id) }"
          >
            <td><span style="font-weight:700;color:var(--ink-40)">{{ conversationProtocol(c) }}</span></td>
            <td>
              <div class="contact-cell">
                <div
                  class="av"
                  style="width:28px;height:28px;font-size:10px;overflow:hidden;"
                  :style="{ background: avatarColor(c.contact.name) }"
                  @mouseenter="showContactPopup(c, $event)"
                  @mouseleave="$emit('hidePopup')"
                >
                  <img v-if="c.contact.thumbnail" :src="c.contact.thumbnail" :alt="c.contact.name" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block;" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
                  <template v-else>{{ initials(c.contact.name) }}</template>
                </div>
                <div>
                  <div class="c-name">{{ c.contact.name }}</div>
                  <div v-if="c.contact.phone" class="c-phone">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.59 2.24h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    {{ fmtPhone(c.contact.phone) }}
                  </div>
                  <div v-if="c.cnpj" class="c-doc">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/>
                    </svg>
                    CNPJ {{ fmtDoc(c.cnpj) }}
                  </div>
                  <div v-if="c.cpf" class="c-doc">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/>
                    </svg>
                    CPF {{ fmtDoc(c.cpf) }}
                  </div>
                </div>
              </div>
            </td>
            <td>
              <div v-if="c.assignee" class="assignee-cell">
                <img
                  v-if="c.assignee.thumbnail"
                  :src="c.assignee.thumbnail"
                  :alt="c.assignee.name"
                  class="av-img"
                  style="width:26px;height:26px;cursor:pointer"
                  @mouseenter="showAssigneePopup(c, $event)"
                  @mouseleave="$emit('hidePopup')"
                />
                <div
                  v-else
                  class="av"
                  style="width:26px;height:26px;font-size:10px;"
                  :style="{ background: avatarColor(c.assignee.name) }"
                  @mouseenter="showAssigneePopup(c, $event)"
                  @mouseleave="$emit('hidePopup')"
                >{{ initials(c.assignee.name) }}</div>
                <span class="a-name">{{ c.assignee.name }}</span>
              </div>
              <span v-else style="color:var(--ink-40);font-size:12px">— sem agente —</span>
            </td>
            <td><span style="font-weight:500;text-transform:capitalize">{{ c.team?.name || '—' }}</span></td>
            <td>
              <span class="status-pill" :class="c.status">
                <span class="sdot"></span>
                {{ statusLabel(c.status) }}
              </span>
            </td>
            <td>
              <template v-if="durOf(c).label !== '—'">
                <span class="dur-cell" :class="durOf(c).type">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <template v-if="durOf(c).type === 'resolved'">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </template>
                    <template v-else-if="durOf(c).type === 'long'">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                    </template>
                    <template v-else>
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </template>
                  </svg>
                  {{ durOf(c).label }}
                </span>
              </template>
              <span v-else style="color:var(--ink-40)">—</span>
            </td>
            <td><span class="wait-cell" :class="waitOf(c).cls">{{ waitOf(c).label }}</span></td>
            <td>
              <div class="label-chips">
                <template v-if="c.labels.length">
                  <span v-for="l in c.labels" :key="l" class="label-chip">{{ l.replace(/_/g, ' ') }}</span>
                </template>
                <span v-else style="color:var(--ink-40);font-size:12px">—</span>
              </div>
            </td>
            <td>
              <span v-if="c.fluxo_id" class="fluxo-pill">{{ fluxoLabel(c.fluxo_id) }}</span>
              <span v-else style="color:var(--ink-40);font-size:12px">—</span>
            </td>
            <td>
              <div style="display:flex;gap:4px;align-items:center;">
                <button class="open-chat-btn" title="Abrir chat" @click="emit('openChat', c)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </button>
                <button class="open-chat-btn" title="Histórico do contato" @click="emit('openHistory', c)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination">
        <div class="pag-left">
          <span class="pag-info">
            {{ filtered.length === 0 ? 'Nenhum resultado' : `${pageStart + 1}–${pageEnd} de ${filtered.length}` }}
          </span>
          <select v-model.number="pageSize" class="pag-size">
            <option :value="10">10 / pág</option>
            <option :value="25">25 / pág</option>
            <option :value="50">50 / pág</option>
            <option :value="100">100 / pág</option>
          </select>
        </div>
        <div class="pag-controls">
          <button class="pag-btn" :disabled="currentPage === 1" @click="goPage(currentPage - 1)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <template v-for="p in pageButtons" :key="p">
            <span v-if="p === '…'" class="pag-ellipsis">…</span>
            <button
              v-else
              class="pag-btn"
              :class="{ active: p === currentPage }"
              @click="goPage(Number(p))"
            >{{ p }}</button>
          </template>
          <button class="pag-btn" :disabled="currentPage === totalPages" @click="goPage(currentPage + 1)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Conversation, PopupData } from '@/types/dashboard'
import { avatarColor, initials, fmtChannel, fmtWait, convDuration, fluxoLabel, conversationProtocol } from '@/composables/useHelpers'
import { fmtPhone, fmtDoc } from '@/utils/contactsFormat'

const props = defineProps<{
  conversations: Conversation[]
  loading?:     boolean
  // Fase 3 do plano de carregamento progressivo (docs/planos/PLANO-SKELETON-DASHBOARD.md):
  // gate do skeleton abaixo. `loading` sozinho tem um vão real — começa
  // `false` até loadData() rodar, então por um instante nem `loading` nem
  // `conversations.length` indicam "ainda carregando". `scopeReady` só vira
  // `true` uma vez (useDashboardEngine.ts), sem esse vão.
  scopeReady?:  boolean
  changedIds?:  Set<number>
}>()
const emit  = defineEmits<{
  showPopup:   [data: PopupData, el: HTMLElement]
  hidePopup:   []
  openChat:    [conv: Conversation]
  openHistory: [conv: Conversation]
}>()

const query        = ref('')
const filterStatus = ref('')
const filterTeam   = ref('')
const filterLabel  = ref('')
const filterFluxo  = ref<number | ''>('')
const dateFrom     = ref('')
const dateTo       = ref('')
const currentPage  = ref(1)
const pageSize     = ref(10)

const teamOptions = computed(() =>
  [...new Set(props.conversations.map(c => c.team?.name || '').filter(Boolean))].sort()
)

const labelOptions = computed(() =>
  [...new Set(props.conversations.flatMap(c => c.labels))].filter(Boolean).sort()
)

const fluxoOptions = computed(() => {
  const ids = [...new Set(props.conversations
    .map(c => c.fluxo_id)
    .filter((id): id is number => id !== null)
  )].sort((a, b) => a - b)
  return ids.map(id => ({ id, name: fluxoLabel(id) }))
})

const filtered = computed(() => {
  const q       = query.value.toLowerCase()
  const tsFrom  = dateFrom.value ? new Date(dateFrom.value).getTime() / 1000 : null
  const tsTo    = dateTo.value   ? new Date(dateTo.value).getTime()   / 1000 + 86399 : null
  return props.conversations.filter(c => {
    if (q && !(
      c.contact.name.toLowerCase().includes(q) ||
      c.contact.phone.toLowerCase().includes(q) ||
      (c.assignee?.name || '').toLowerCase().includes(q) ||
      (c.team?.name    || '').toLowerCase().includes(q)
    )) return false
    if (filterStatus.value && c.status !== filterStatus.value) return false
    if (filterTeam.value   && (c.team?.name || '') !== filterTeam.value) return false
    if (filterLabel.value  && !c.labels.includes(filterLabel.value)) return false
    if (filterFluxo.value !== '' && c.fluxo_id !== filterFluxo.value) return false
    if (tsFrom && c.created_at < tsFrom) return false
    if (tsTo   && c.created_at > tsTo)   return false
    return true
  })
})

watch(filtered, () => { currentPage.value = 1 })

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
const pageStart  = computed(() => (currentPage.value - 1) * pageSize.value)
const pageEnd    = computed(() => Math.min(pageStart.value + pageSize.value, filtered.value.length))
const pageConvs  = computed(() => filtered.value.slice(pageStart.value, pageEnd.value))

const pageButtons = computed(() => {
  const total = totalPages.value
  const cur   = currentPage.value
  const WING  = 2
  const pages: (number | string)[] = []
  let prev: number | null = null
  for (let p = 1; p <= total; p++) {
    if (p === 1 || p === total || (p >= cur - WING && p <= cur + WING)) {
      if (prev !== null && p - prev > 1) pages.push('…')
      pages.push(p)
      prev = p
    }
  }
  return pages
})

function goPage(p: number) {
  currentPage.value = Math.max(1, Math.min(p, totalPages.value))
}
function clearDates() {
  dateFrom.value = ''
  dateTo.value   = ''
}
function statusLabel(s: string) {
  const map: Record<string, string> = { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente', snoozed: 'Adiada' }
  return map[s] || s
}
function durOf(c: Conversation) { return convDuration(c) }
function waitOf(c: Conversation) {
  return fmtWait(c.waiting_since)
}

function showContactPopup(c: Conversation, e: MouseEvent) {
  emit('showPopup', {
    name:    c.contact.name,
    role:    'Contato',
    phone:   c.contact.phone,
    channel: fmtChannel(c.channel),
    team:    c.team?.name || null,
    status:  c.status,
    labels:  c.labels,
  }, e.currentTarget as HTMLElement)
}
function showAssigneePopup(c: Conversation, e: MouseEvent) {
  if (!c.assignee) return
  emit('showPopup', {
    name:      c.assignee.name,
    role:      c.assignee.role,
    email:     c.assignee.email  || '',
    thumbnail: c.assignee.thumbnail || '',
    team:      c.team?.name || null,
    status:    c.assignee.availability || 'offline',
  }, e.currentTarget as HTMLElement)
}
</script>
